import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { BillingFeature, GranulationTypeId, Theme } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import didInsert from 'webapp/modifiers/did-insert';
import onHoverPopup from 'webapp/modifiers/on-hover-popup';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormCheckboxSwitchComponent from 'webapp/components/form/checkbox-switch';
import MetricDetailsExploreDataAnomalyDetectionPopupConfigurationComponent, {
	type AnomalyConfiguration,
} from 'webapp/components/metric-details/explore/data/anomaly-detection/popup-configuration';
import MetricDetailsExploreDataAnomalyDetectionPopupErrorComponent from 'webapp/components/metric-details/explore/data/anomaly-detection/popup-error';
import MetricDetailsExploreDataAnomalyDetectionPopupForbiddenFeatureComponent from 'webapp/components/metric-details/explore/data/anomaly-detection/popup-forbidden-feature';

import type { MetricSetup } from 'webapp/models-custom';
import type DateRange from 'webapp/models-custom/date-range';
import type StatusObject from 'webapp/models-custom/status-object';

interface Args {
	metricSetup: MetricSetup;
	dateRange: DateRange | null;
	isAnomalySwitchChecked: boolean;
	anomalyConfiguration: AnomalyConfiguration;
	anomalyStatusObjectInErrorOrInfo?: StatusObject;
	changeAnomalySwitchState: (newValue: boolean) => void;
	changeAnomalyConfiguration: (configuration: AnomalyConfiguration) => void;
}

export default class MetricDetailsExploreDataAnomalyDetectionAnomalySwitchComponent extends Component<Args> {
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;

	get isAnomalyDetectionEnabled() {
		return this.billingService.checkIfCan(BillingFeature.ANOMALY_DETECTION);
	}

	get isSingleOutput() {
		return this.args.metricSetup.isSingleOutput;
	}

	get isAnomalyDetectionCheckboxSelected() {
		return this.args.isAnomalySwitchChecked;
	}

	get granulationTypeId() {
		return this.args.dateRange?.granulationTypeId;
	}

	get isAnomalyDetectionSwitchDisabled() {
		return !this.isAnomalyDetectionEnabled || !this.isGranulationAndDimensionValid;
	}

	get anomalyStatusObjectInErrorOrInfo() {
		return this.args.anomalyStatusObjectInErrorOrInfo;
	}

	get isGranulationValid() {
		const g = this.args.dateRange?.granulationTypeId;
		return (
			g === GranulationTypeId.DAILY ||
			g === GranulationTypeId.WEEKLY ||
			g === GranulationTypeId.MONTHLY
		);
	}

	get isGranulationAndDimensionValid() {
		return this.isSingleOutput && this.isGranulationValid;
	}

	get anomaliesSuffixIcon() {
		if (!this.isAnomalyDetectionEnabled) {
			return 'Lock';
		} else if (!this.isGranulationAndDimensionValid) {
			return 'InfoFill';
		} else if (this.isAnomalyDetectionEnabled) {
			return 'Settings';
		} else {
			return;
		}
	}

	get theme() {
		return this.navigationService.theme.id || Theme.LIGHT;
	}

	get showConfiguration() {
		return this.isAnomalyDetectionEnabled && this.isSingleOutput && this.isGranulationValid;
	}

	@action
	handleApplyClick(args: AnomalyConfiguration) {
		this.args.changeAnomalyConfiguration(args);
		void this.popupService.activePopup?.close();
	}

	@action
	handleRetryClick() {
		this.args.changeAnomalyConfiguration(this.args.anomalyConfiguration);
	}

	@action
	changeAnomalySwitchState() {
		this.args.changeAnomalySwitchState(!this.args.isAnomalySwitchChecked);
	}

	@action
	onIconClick(event: MouseEvent) {
		const popup = this.popupService.createContext(
			MetricDetailsExploreDataAnomalyDetectionPopupConfigurationComponent,
			event.target as HTMLElement,
			{
				popupClass: 'popup-anomaly-detection-configuration',
				theme: this.theme,
			},
		);

		popup.open({
			isSingleOutput: this.isSingleOutput,
			anomalyConfiguration: this.args.anomalyConfiguration,
			granulationTypeId: this.granulationTypeId,
			onApplyClick: this.handleApplyClick,
		});
	}

	@action
	onIconHover(node: HTMLElement) {
		const popup = this.popupService.createContext(
			MetricDetailsExploreDataAnomalyDetectionPopupForbiddenFeatureComponent,
			node,
			{
				popupClass: 'popup-anomaly-forbidden-feature',
				theme: this.theme,
			},
		);

		let title = 'Highlight Data anomalies';
		let description =
			'Anomaly detection is a statistical technique used to identify outliers in time-series data at specific points in time.';

		if (this.isAnomalyDetectionEnabled) {
			title =
				'Highlighting Data anomalies is available only for one-dimensional metrics on line/bar charts for selected granulations.';
			description =
				'Select a line or bar chart by day, week, or month, with non-dimensional metric, or pick a single dimension to highlight anomalies.';
		}

		popup.open({
			title,
			description,
			billingFeature: BillingFeature.ANOMALY_DETECTION,
			learnMoreLink: 'https://help.databox.com/detect-anomalies-in-your-data',
			sendTrackingEvent: !this.isAnomalyDetectionEnabled,
			showUpgradeLink: !this.isAnomalyDetectionEnabled,
		});
	}

	@action
	onIconAttentionHover(node: HTMLElement) {
		const popup = this.popupService.createContext(
			MetricDetailsExploreDataAnomalyDetectionPopupErrorComponent,
			node,
			{
				popupClass: 'popup-anomaly-detection-error',
				theme: this.theme,
			},
		);

		popup.open({
			onRetryClick: this.handleRetryClick,
			anomalyStatusObjectInErrorOrInfo: this.anomalyStatusObjectInErrorOrInfo,
		});
	}

	@action
	trackErrorEvent() {
		const metricSetup = this.args.metricSetup,
			metricName = metricSetup.metric?.name;

		this.analyticsService.event(TrackingEvent.ANOMALY_DETECTION_ERROR, {
			datasource: metricSetup.sourceType?.key,
			metrics: metricName ? [metricName] : [],
			granulation: this.args.dateRange?.granulationTypeId,
			date_range: this.args.dateRange?.rangeTypeId,
			type: this.anomalyStatusObjectInErrorOrInfo?.type,
		});
	}

	@action
	onClick() {
		if (this.isAnomalyDetectionSwitchDisabled) {
			this.billingService.checkIfCan(BillingFeature.ANOMALY_DETECTION, true);
		}
	}
	<template>
		<div class="anomaly-switch">
			<div class="switch-wrapper" role="button" {{on "click" this.onClick}}>
				<FormCheckboxSwitchComponent
					@name="anomaly-switch"
					@selected={{this.isAnomalyDetectionCheckboxSelected}}
					@onChange={{this.changeAnomalySwitchState}}
					@theme={{this.theme}}
					@size="small"
					@isDisabled={{this.isAnomalyDetectionSwitchDisabled}}
				/>
			</div>

			<span>
				Highlight anomalies

				{{#if this.showConfiguration}}
					<span
						role="button"
						class="suffix-icon {{this.anomaliesSuffixIcon}}"
						{{on "click" this.onIconClick}}
					>
						{{#if this.anomaliesSuffixIcon}}
							<CommonUiIconComponent @name={{this.anomaliesSuffixIcon}} @size="sm" />
						{{/if}}
					</span>
				{{else}}
					<span
						role="button"
						class="suffix-icon {{this.anomaliesSuffixIcon}}"
						{{onHoverPopup this.onIconHover}}
					>
						{{#if this.anomaliesSuffixIcon}}
							<CommonUiIconComponent @name={{this.anomaliesSuffixIcon}} @size="sm" />
						{{/if}}
					</span>
				{{/if}}
			</span>

			{{#if this.anomalyStatusObjectInErrorOrInfo}}
				<span
					role="button"
					class="attention-icon"
					{{onHoverPopup this.onIconAttentionHover}}
					{{didInsert this.trackErrorEvent}}
				>
					<CommonUiIconComponent @name="AlertFill" @size="sm" />
				</span>
			{{/if}}
		</div>
	</template>
}
